import type { ModuleViewSla, ModuleViewSlaOutcome } from './types'

/**
 * EXIBIÇÃO do SLA no contrato ModuleView (spec 2026-10-01 §7.3). Genérico (nenhum domínio): o
 * backend decide prazo e janela; aqui só se reclassifica no tique com `attentionSeconds`, com
 * as mesmas bordas inclusivas da §3.5, e se escreve o texto do mockup.
 */

export type ModuleViewSlaLiveState = 'ON_TIME' | 'ATTENTION' | 'OVERDUE' | 'DONE' | 'NO_SLA'

export type ModuleViewSlaTone =
  | 'on-time'
  | 'attention'
  | 'overdue'
  | 'done-ok'
  | 'done-neutral'
  | 'none'

export const MODULE_VIEW_SLA_LABELS: Record<Exclude<ModuleViewSlaLiveState, 'DONE'>, string> = {
  ON_TIME: 'No prazo',
  ATTENTION: 'Atenção',
  OVERDUE: 'Vencida',
  NO_SLA: 'Sem SLA'
}

export const MODULE_VIEW_SLA_DONE_LABELS: Record<ModuleViewSlaOutcome | 'UNKNOWN', string> = {
  ON_TIME: 'Concluído no prazo',
  LATE: 'Concluído fora do prazo',
  UNKNOWN: 'Concluído'
}

export interface ModuleViewSlaView {
  state: ModuleViewSlaLiveState
  outcome: ModuleViewSlaOutcome | null
  label: string
  text: string
  tone: ModuleViewSlaTone
}

const deadlineMsOf = (sla: ModuleViewSla): number =>
  sla.deadlineAt ? Date.parse(sla.deadlineAt) : Number.NaN

export function liveRemainingSeconds(sla: ModuleViewSla, nowMs: number | null): number | null {
  const deadlineMs = deadlineMsOf(sla)
  if (nowMs === null || !Number.isFinite(deadlineMs)) return sla.remainingSeconds
  return Math.floor((deadlineMs - nowMs) / 1000)
}

export function liveSlaState(
  sla: ModuleViewSla | null,
  nowMs: number | null
): ModuleViewSlaLiveState {
  if (!sla || sla.state === 'NOT_STARTED') return 'NO_SLA'
  if (sla.state === 'DONE') return 'DONE'
  const serverState = sla.state === 'URGENT' ? 'ATTENTION' : sla.state
  return clockSlaState(sla, nowMs) ?? serverState
}

/**
 * Reclassifica pelo relógio com as bordas inclusivas da §3.5. `null` quando falta relógio,
 * prazo válido ou `attentionSeconds` — aí vale o estado do servidor (nunca "NaN").
 */
function clockSlaState(
  sla: ModuleViewSla,
  nowMs: number | null
): 'ON_TIME' | 'ATTENTION' | 'OVERDUE' | null {
  const deadlineMs = deadlineMsOf(sla)
  const attention = sla.attentionSeconds ?? null
  if (nowMs === null || !Number.isFinite(deadlineMs) || attention === null) return null
  const remainingMs = deadlineMs - nowMs
  if (remainingMs <= 0) return 'OVERDUE'
  if (remainingMs <= attention * 1000) return 'ATTENTION'
  return 'ON_TIME'
}

/** `N min` (mínimo 1) até 1 h; `N h` até 48 h; depois `N d`. */
export function formatSlaDuration(seconds: number): string {
  const abs = Math.abs(Math.trunc(seconds))
  if (abs < 3600) return `${Math.max(1, Math.floor(abs / 60))} min`
  if (abs < 48 * 3600) return `${Math.floor(abs / 3600)} h`
  return `${Math.floor(abs / 86400)} d`
}

export function formatSlaRelative(remainingSeconds: number): string {
  const duration = formatSlaDuration(remainingSeconds)
  return remainingSeconds <= 0 ? `venceu há ${duration}` : `vence em ${duration}`
}

const OPEN_TONES: Record<'ON_TIME' | 'ATTENTION' | 'OVERDUE', ModuleViewSlaTone> = {
  ON_TIME: 'on-time',
  ATTENTION: 'attention',
  OVERDUE: 'overdue'
}

export function describeModuleViewSla(
  sla: ModuleViewSla | null,
  nowMs: number | null
): ModuleViewSlaView {
  const state = liveSlaState(sla, nowMs)
  if (state === 'NO_SLA' || !sla) {
    return {
      state: 'NO_SLA',
      outcome: null,
      label: MODULE_VIEW_SLA_LABELS.NO_SLA,
      text: MODULE_VIEW_SLA_LABELS.NO_SLA,
      tone: 'none'
    }
  }
  if (state === 'DONE') {
    const outcome = sla.outcome ?? null
    const label = MODULE_VIEW_SLA_DONE_LABELS[outcome ?? 'UNKNOWN']
    return {
      state,
      outcome,
      label,
      text: label,
      tone: outcome === 'ON_TIME' ? 'done-ok' : 'done-neutral'
    }
  }
  const label = MODULE_VIEW_SLA_LABELS[state]
  const remaining = liveRemainingSeconds(sla, nowMs)
  const text = remaining === null ? label : `${label} · ${formatSlaRelative(remaining)}`
  return { state, outcome: null, label, text, tone: OPEN_TONES[state] }
}
